/**
 * QuickAdd type config — single source of truth for the 7 entity
 * types the universal QuickAdd modal can create.
 *
 * Extracted from QuickAddModal.js so:
 *   1. Adding a new entity type is a one-file change (add an entry
 *      to TYPES, a case in `secondaryFieldFor`, and a case in
 *      `buildPayload` — all colocated).
 *   2. The modal component stays focused on render + interaction
 *      (it's no longer carrying ~120 lines of payload mapping).
 *   3. Unit-testing payload shape doesn't require mounting React.
 *
 * Adding a new type:
 *   - Add to TYPES[] (key, label, icon, accent).
 *   - If it needs a secondary input, add a case in `secondaryFieldFor`.
 *   - Add a case in `buildPayload` returning [path, body].
 */
import {
  Target, Phone, FileText, BookOpen, CreditCard, Handshake, Calendar,
} from 'lucide-react';
import { todayIST } from '../../utils/locale';

export const TYPES = [
  // NOTE on accent colors: each chip gets a distinct hue so the seven
  // types are visually distinguishable at a glance. The Task chip
  // previously used `var(--pd-accent)` which is an undefined CSS
  // variable — that resolved to transparent, making the active Task
  // chip invisible (white-on-transparent). All seven now use real
  // hex values from a 7-step categorical palette; Payment uses
  // `--pd-crimson` because that variable IS defined and tracks the
  // theme correctly.
  { key: 'task',       label: 'Task',       icon: Target,    accent: '#F59E0B' },  // amber (brand)
  { key: 'call',       label: 'Call',       icon: Phone,     accent: '#10b981' },  // emerald
  { key: 'note',       label: 'Note',       icon: FileText,  accent: '#8b5cf6' },  // violet
  { key: 'reading',    label: 'Reading',    icon: BookOpen,  accent: '#f97316' },  // orange
  { key: 'payment',    label: 'Payment',    icon: CreditCard, accent: 'var(--pd-crimson)' },  // crimson (theme-aware)
  { key: 'commitment', label: 'Commitment', icon: Handshake, accent: '#0ea5e9' },  // sky
  { key: 'event',      label: 'Event',      icon: Calendar,  accent: '#ec4899' },  // pink
];

export const TYPE_BY_KEY = Object.fromEntries(TYPES.map((t) => [t.key, t]));

/**
 * Per-type optional secondary input. The primary input is always the
 * textarea title; this returns the *additional* field shown directly
 * below it when a type is selected.
 *
 * Returns `{ label, placeholder, value, onChange, testid, type? }` or
 * `null` when no secondary field is needed (e.g. plain `task`/`note`).
 */
export function secondaryFieldFor(type, state, setState) {
  switch (type) {
    case 'payment':
      return {
        label: 'Amount (optional)',
        placeholder: 'e.g. 4500',
        type: 'number',
        value: state.amount ?? '',
        onChange: (v) => setState((s) => ({ ...s, amount: v })),
        testid: 'quickadd-amount',
      };
    case 'reading':
      return {
        label: 'URL',
        placeholder: 'https://…',
        value: state.url ?? '',
        onChange: (v) => setState((s) => ({ ...s, url: v })),
        testid: 'quickadd-url',
      };
    case 'call':
      return {
        label: 'Contact (optional)',
        placeholder: 'e.g. Sam Patel',
        value: state.contactName ?? '',
        onChange: (v) => setState((s) => ({ ...s, contactName: v })),
        testid: 'quickadd-contact',
      };
    case 'commitment':
      return {
        label: 'Stakeholder (optional)',
        placeholder: 'Who are you committing to?',
        value: state.stakeholder ?? '',
        onChange: (v) => setState((s) => ({ ...s, stakeholder: v })),
        testid: 'quickadd-stakeholder',
      };
    case 'event':
      return {
        label: 'When',
        placeholder: '',
        type: 'datetime-local',
        value: state.scheduledFor ?? '',
        onChange: (v) => setState((s) => ({ ...s, scheduledFor: v })),
        testid: 'quickadd-scheduledfor',
      };
    default:
      return null;
  }
}

/**
 * Build the POST [path, payload] tuple for the chosen type. Mirrors
 * the Pydantic Create models in `backend/models.py`.
 *
 * Defaults chosen to match what the existing detail views write:
 *   - tasks → status TODAY, priority MEDIUM (so it shows up in today's queue)
 *   - calls → status QUEUED
 *   - payments → status UPCOMING, currency INR, amount 0 if not parsed
 *   - commitments → status OPEN
 *   - event → task with status SCHEDULED + scheduledDate
 */
export function buildPayload(type, state) {
  const title = (state.title || '').trim();
  switch (type) {
    case 'task':
      return ['/tasks', { title, status: 'TODAY', priority: 'MEDIUM' }];
    case 'call':
      return ['/calls', {
        contactName: state.contactName?.trim() || title,
        purpose: state.contactName ? title : '',
        status: 'QUEUED',
      }];
    case 'note':
      return ['/notes', { title, content: '' }];
    case 'reading':
      return ['/reading', {
        url: state.url?.trim() || '',
        title,
        description: '',
      }];
    case 'payment': {
      const amt = parseFloat(state.amount);
      return ['/payments', {
        title,
        amount: Number.isFinite(amt) ? amt : 0,
        currency: 'INR',
        status: 'UPCOMING',
      }];
    }
    case 'commitment':
      return ['/commitments', {
        title,
        stakeholder: state.stakeholder?.trim() || '',
        status: 'OPEN',
      }];
    case 'event': {
      // Calendar event = task with scheduledDate. The backend stores
      // YYYY-MM-DD; the datetime-local input gives YYYY-MM-DDThh:mm.
      // We slice off the time portion safely.
      const raw = (state.scheduledFor || '').trim();
      const [datePart] = raw ? raw.split('T') : [todayIST()];
      return ['/tasks', {
        title,
        status: 'SCHEDULED',
        priority: 'MEDIUM',
        scheduledDate: datePart,
      }];
    }
    default:
      // Unknown type → drop into inbox as a safety net. Should never
      // happen because the chip set is closed, but defensive.
      return ['/inbox', { title }];
  }
}
